/**
 * Account — the operator's personal settings page.
 *
 * Sections (top→bottom):
 *   1. Profile  — inline-editable display name; email is read-only.
 *   2. Workspace — read-only summary of the active tenant.
 *   3. Preferences — local theme toggle.
 *   4. Security — change-password form + sign-out.
 *
 * Wiring:
 *   - Display name → PATCH /v1/me   (useUpdateProfile)
 *   - Password     → POST /v1/me/password   (useChangePassword)
 *   - Theme        → useTheme()  (localStorage only)
 *   - Sign out     → useLogout()
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  Check,
  ChevronRight,
  Eye,
  EyeOff,
  LogOut,
  Moon,
  Pencil,
  Shield,
  Sun,
  User as UserIcon,
  X,
} from 'lucide-react';
import { ChangePasswordRequestSchema, type ChangePasswordRequest } from '@seo/types';

import { Page, PageHeader } from '@/components/ui/page';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { Surface } from '@/components/ui/surface';
import { TextField, FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { SettingsTabs } from '@/components/settings-tabs';

import { useAuthStore } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { useLogout } from '@/features/auth/use-logout';
import { useUpdateProfile } from '@/features/auth/use-update-profile';
import { useChangePassword } from '@/features/auth/use-change-password';
import { summarizeApiError, extractFieldErrors } from '@/lib/api';
import { cn } from '@/lib/cn';

export function AccountRoute(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const role = useAuthStore((s) => s.role);

  const initials = (user?.display_name ?? user?.email ?? 'A')
    .slice(0, 1)
    .toUpperCase();

  return (
    <Page>
      <PageHeader
        eyebrow="Settings"
        title="Account"
        description="Your profile, preferences, and session security."
      />
      <SettingsTabs />

      {/* Profile card with editable name */}
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              Profile
            </SectionTitle>
            <SectionDescription>
              How you appear across this workspace.
            </SectionDescription>
          </div>
        </SectionHeader>

        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-lg font-semibold text-primary-foreground">
            {initials}
          </div>
          <div className="min-w-0 flex-1 space-y-4">
            <DisplayNameField
              currentName={user?.display_name ?? ''}
              email={user?.email ?? ''}
            />
            <FormField
              label="Email"
              hint="Used to sign in. Email changes aren't supported yet — contact support if you need it changed."
            >
              <Input value={user?.email ?? ''} readOnly className="bg-muted/30" />
            </FormField>
            {role ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Role in this workspace:
                <Badge variant="secondary" className="capitalize">
                  {role}
                </Badge>
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      {/* Workspace */}
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Workspace</SectionTitle>
            <SectionDescription>
              The active tenant this account is operating inside.
            </SectionDescription>
          </div>
        </SectionHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <InfoTile label="Workspace" value={tenant?.name ?? '—'} />
          <InfoTile label="Slug" value={tenant?.slug ?? '—'} mono />
          <InfoTile label="Plan" value={tenant?.plan ?? 'trial'} capitalize />
          <InfoTile label="Your role" value={role ?? '—'} capitalize />
        </div>
      </Section>

      {/* Preferences — theme */}
      <PreferencesSection />

      {/* Security — change password + sign out */}
      <SecuritySection />
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Inline-editable display name                                         */
/* ------------------------------------------------------------------ */

function DisplayNameField({
  currentName,
  email,
}: {
  currentName: string;
  email: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName);
  const update = useUpdateProfile();

  // Keep the local input in sync if the auth store value changes from elsewhere.
  useEffect(() => {
    if (!editing) setValue(currentName);
  }, [currentName, editing]);

  const trimmed = value.trim();
  const dirty = trimmed !== currentName;
  const tooShort = trimmed.length === 0;
  const tooLong = trimmed.length > 80;
  const invalid = tooShort || tooLong;

  const cancel = (): void => {
    setValue(currentName);
    setEditing(false);
  };

  const save = async (): Promise<void> => {
    if (invalid || !dirty) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ display_name: trimmed });
      toast.success('Profile updated');
      setEditing(false);
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not update profile',
        fieldLabels: { display_name: 'Display name' },
      });
      toast.error(summary.title, {
        description: fieldErrors.display_name ?? summary.description,
      });
    }
  };

  if (!editing) {
    return (
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <Label className="text-sm font-medium">Display name</Label>
          <div className="truncate text-base text-foreground">
            {currentName || (
              <span className="italic text-muted-foreground">
                {email.split('@')[0] ?? 'Unnamed'}
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(true)}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </div>
    );
  }

  return (
    <FormField
      label="Display name"
      error={
        tooShort
          ? 'Display name is required'
          : tooLong
            ? 'Keep it under 80 characters'
            : undefined
      }
      hint="Shown next to your activity and in mentions."
    >
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={value}
          maxLength={120}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
        />
        <Button
          variant="ghost"
          size="icon"
          aria-label="Cancel"
          onClick={cancel}
          disabled={update.isPending}
        >
          <X className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={update.isPending || invalid || !dirty}
        >
          {update.isPending ? (
            <Spinner className="mr-1.5" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Save
        </Button>
      </div>
    </FormField>
  );
}

/* ------------------------------------------------------------------ */
/* Workspace info tiles                                                 */
/* ------------------------------------------------------------------ */

function InfoTile({
  label,
  value,
  mono = false,
  capitalize = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  capitalize?: boolean;
}): JSX.Element {
  return (
    <Surface tone="muted" className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 truncate text-sm font-semibold',
          mono && 'font-mono font-normal',
          capitalize && 'capitalize',
        )}
      >
        {value}
      </div>
    </Surface>
  );
}

/* ------------------------------------------------------------------ */
/* Preferences (theme)                                                  */
/* ------------------------------------------------------------------ */

function PreferencesSection(): JSX.Element {
  const { theme, setTheme } = useTheme();

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>Preferences</SectionTitle>
          <SectionDescription>Stored on this device.</SectionDescription>
        </div>
      </SectionHeader>

      <div className="space-y-1.5">
        <div className="text-sm font-medium">Appearance</div>
        <div className="inline-flex rounded-lg border border-border bg-card p-1">
          {(
            [
              { v: 'light', label: 'Light', icon: Sun },
              { v: 'dark', label: 'Dark', icon: Moon },
            ] as const
          ).map((opt) => {
            const Icon = opt.icon;
            const active = theme === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setTheme(opt.v)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Security — password change + sign out                                */
/* ------------------------------------------------------------------ */

function SecuritySection(): JSX.Element {
  const [open, setOpen] = useState(false);
  const logout = useLogout();

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            Security
          </SectionTitle>
          <SectionDescription>
            Manage your password and active session.
          </SectionDescription>
        </div>
      </SectionHeader>

      <div className="space-y-3">
        {/* Collapsible change-password panel */}
        <div className="rounded-md border border-border/70 bg-card">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">Password</div>
              <div className="text-xs text-muted-foreground">
                Update the password used to sign in.
              </div>
            </div>
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-90',
              )}
            />
          </button>
          {open ? (
            <div className="border-t border-border/70 p-3">
              <ChangePasswordForm onDone={() => setOpen(false)} />
            </div>
          ) : null}
        </div>

        {/* Sign out */}
        <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">Sign out</div>
            <div className="text-xs text-muted-foreground">
              End your session on this device.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => logout.mutate()}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </Button>
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Change-password form (inside the Security section)                   */
/* ------------------------------------------------------------------ */

interface ChangePasswordFormValues extends ChangePasswordRequest {
  confirm_password: string;
}

function ChangePasswordForm({ onDone }: { onDone: () => void }): JSX.Element {
  const change = useChangePassword();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Validate the API-bound fields via the shared schema. `confirm_password`
  // is a UI-only field — handled manually in the submit handler so the API
  // payload stays clean.
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(ChangePasswordRequestSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const newPassword = watch('new_password') ?? '';
  const strength = scorePassword(newPassword);

  const submit = handleSubmit(async (values) => {
    if (values.new_password !== values.confirm_password) {
      setError('confirm_password', {
        type: 'manual',
        message: "Confirmation doesn't match the new password",
      });
      return;
    }
    try {
      await change.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success('Password updated');
      reset();
      onDone();
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not change password',
        fieldLabels: {
          current_password: 'Current password',
          new_password: 'New password',
        },
      });
      if (summary.code === 'AUTH_CURRENT_PASSWORD_WRONG') {
        setError('current_password', {
          type: 'server',
          message: 'That password is incorrect',
        });
      }
      if (fieldErrors.new_password) {
        setError('new_password', { type: 'server', message: fieldErrors.new_password });
      }
      if (fieldErrors.current_password) {
        setError('current_password', {
          type: 'server',
          message: fieldErrors.current_password,
        });
      }
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      <TextField
        label="Current password"
        type={showCurrent ? 'text' : 'password'}
        autoComplete="current-password"
        autoFocus
        error={errors.current_password?.message}
        {...register('current_password')}
      />
      <button
        type="button"
        onClick={() => setShowCurrent((v) => !v)}
        className="-mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        {showCurrent ? (
          <EyeOff className="h-3 w-3" />
        ) : (
          <Eye className="h-3 w-3" />
        )}
        {showCurrent ? 'Hide' : 'Show'} current password
      </button>

      <TextField
        label="New password"
        type={showNew ? 'text' : 'password'}
        autoComplete="new-password"
        placeholder="At least 10 characters"
        error={errors.new_password?.message}
        {...register('new_password')}
      />
      <div className="-mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setShowNew((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {showNew ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showNew ? 'Hide' : 'Show'} new password
        </button>
        {newPassword.length > 0 ? <StrengthMeter score={strength} /> : null}
      </div>

      <TextField
        label="Confirm new password"
        type={showNew ? 'text' : 'password'}
        autoComplete="new-password"
        error={errors.confirm_password?.message}
        {...register('confirm_password')}
      />

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDone}
          disabled={isSubmitting || change.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting || change.isPending}
        >
          {isSubmitting || change.isPending ? (
            <Spinner className="mr-1.5" />
          ) : null}
          Change password
        </Button>
      </div>
    </form>
  );
}

/**
 * Naive password strength scorer (0..4). Doesn't replace zxcvbn; good enough
 * to nudge the user away from obviously weak picks.
 */
function scorePassword(pw: string): number {
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

function StrengthMeter({ score }: { score: number }): JSX.Element {
  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;
  const tones = [
    'bg-destructive',
    'bg-destructive',
    'bg-warning',
    'bg-success',
    'bg-success',
  ] as const;
  return (
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
      <div className="flex gap-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-1 w-5 rounded-full transition-colors',
              i < score ? tones[score] : 'bg-border',
            )}
          />
        ))}
      </div>
      <span>{labels[score]}</span>
    </div>
  );
}
